import BasePopover from '@risk-smart/themed-cloudscape-components/popover';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import type { FC } from 'react';

import { toLocalDateTime } from '@/utils/dateUtils';
import { hasColor } from '@/utils/utils';

import styles from './style.module.scss';

/**
 * Tallest a bar may render. Must stay <= the `.badgeContainer` height in
 * style.module.scss, which is sized to a single-line table cell so history columns
 * do not make register rows taller.
 */
const BAR_MAX_HEIGHT_PX = 20;

/** Floor so a zero rating still renders a hoverable nub rather than nothing. */
const BAR_MIN_HEIGHT_PX = 3;

interface ResponsiveRatingBadgesProps {
  ratings?: {
    color?: string;
    label: string;
    tooltip?: string;
    id?: string;
    rating: number;
    testDate: string;
    onClick?: () => void;
  }[];
  invertRating: boolean;
  maxRating: number;
}

interface ResponsiveRatingBadgeProps {
  rating?: {
    color?: string;
    label: string;
    tooltip?: string;
    rating: number;
    testDate: string;
    onClick?: () => void;
  };
  invertRating: boolean;
  maxRating: number;
}

const ResponsiveRatingBadges: FC<ResponsiveRatingBadgesProps> = ({
  ratings,
  maxRating,
  invertRating,
}) => {
  if (!ratings) {
    return '-';
  }
  const className = styles.badgeContainer;

  return (
    <div {...{ className }}>
      {ratings.map((c) => {
        return (
          <ResponsiveRatingBadge
            key={c.id}
            rating={c}
            maxRating={maxRating}
            invertRating={invertRating}
          />
        );
      })}
    </div>
  );
};

const ResponsiveRatingBadge: FC<ResponsiveRatingBadgeProps> = ({
  rating,
  maxRating,
  invertRating,
}) => {
  if (!rating) {
    return '-';
  }
  const title = rating.tooltip;
  const _color = hasColor(rating) ? rating.color : undefined;
  const barClass = styles.bar;
  // `maxRating` is 0 whenever the register's rating options carry no numeric value
  // (see pages/controls/config.tsx), which would divide by zero. There is no scale
  // to plot against in that case, so every bar falls back to the minimum.
  const hasScale = maxRating > 0;
  const percentage = hasScale ? rating.rating / maxRating : 0;
  const scaled = invertRating ? 1 - percentage : percentage;
  // Clamped because a rating can sit outside the current scale — an org that
  // narrows its rating options leaves historical ratings above `maxRating` — and
  // an unclamped bar would escape the container this fix exists to keep it inside.
  const height = hasScale
    ? Math.min(
        BAR_MAX_HEIGHT_PX,
        Math.max(BAR_MIN_HEIGHT_PX, BAR_MAX_HEIGHT_PX * scaled)
      )
    : BAR_MIN_HEIGHT_PX;
  const style = {
    ...getColorStyles(_color),
    height: `${height}px`,
  };

  return (
    <div>
      <BasePopover
        dismissButton={true}
        position={'top'}
        size={'small'}
        header={title}
        triggerType={'text'}
        content={
          <div
            onClick={rating.onClick}
            className={rating.onClick ? 'cursor-pointer' : ''}
          >
            <SpaceBetween direction={'horizontal'} size={'xs'}>
              <span
                className={
                  'no-underline hover:underline font-bold hover:text-teal'
                }
              >
                {toLocalDateTime(rating.testDate)}
              </span>
              <span className={'no-underline hover:underline hover:text-teal'}>
                {rating.label}
              </span>
            </SpaceBetween>
          </div>
        }
      >
        <div {...{ className: barClass }} {...{ style }} title={title} />
      </BasePopover>
    </div>
  );
};

export default ResponsiveRatingBadges;
